import { useCallback, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { loadDocumentResource } from '@/utils/documentResource';
import { offlineDocumentRequest } from '@/utils/offlineDocument';

// Document-only lifetime. Photo/POD media consumers retain their existing hook.
export function useDocumentResource({ url, documentKey, orderNumber }) {
  const session = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const request = offlineDocumentRequest(url);
  const identity = useMemo(() => ({ url: request.url, requestedUrl: url, offline: request.offline, session,
    documentKey, orderNumber }), [request.url, request.offline, url, session, documentKey, orderNumber]);
  const [attempt, setAttempt] = useState(0);
  const owner = useMemo(() => ({ identity, attempt }), [identity, attempt]);
  const latest = useRef(owner);
  latest.current = owner;
  const [result, setResult] = useState(null);

  useLayoutEffect(() => {
    const controller = new AbortController();
    let objectUrl;
    const current = () => !controller.signal.aborted && latest.current === owner
      && getActiveSessionRevision() === identity.session;
    if (identity.url) {
      Promise.resolve().then(() => {
        if (!current()) return null;
        return loadDocumentResource(identity.url, { signal: controller.signal });
      }).then(value => {
        if (!value || !current()) return;
        if (value.state === 'available' && value.blob) {
          objectUrl = URL.createObjectURL(value.blob);
          setResult({ owner, value: { state: value.state, url: objectUrl, contentType: value.contentType } });
        } else setResult({ owner, value });
      }).catch(error => {
        if (current() && error?.name !== 'AbortError') setResult({ owner, value: { state: 'error' } });
      });
    }
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [owner, identity]);

  const retry = useCallback(() => {
    if (latest.current === owner && getActiveSessionRevision() === identity.session) {
      setAttempt(value => value + 1);
    }
  }, [owner, identity]);
  const visible = result?.owner === owner && session === getActiveSessionRevision()
    ? result.value : { state: identity.url ? 'loading' : 'unavailable' };
  return { ...visible, offline: identity.offline, identity, retry };
}
